import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { fetchEmployees } from "../../../data/employees/employees";
import classes from "./Bubble.module.scss";
import { getBubblesCoordinates } from "./bubbleCoordinates";
import { BubblePerson, PositionKey } from "./bubbleData";
import Bubble from "./Bubble";

type Props = React.ComponentPropsWithoutRef<"div">;

const BubblesContainer = ({ ...htmlProps }: Props) => {
  const { t } = useTranslation("team");
  const [bubbleFilter, setBubbleFilter] = useState<PositionKey | null>(null);
  const [bubbleData, setBubbleData] = useState<BubblePerson[]>([]);
  const { locale } = useRouter();

  const { data } = useQuery(
    [
      "employees",
      {
        locale: locale!,
      },
    ],
    fetchEmployees,
    {
      enabled: !!locale,
    }
  );

  const shuffle = (array: BubblePerson[]) => {
    array.forEach((_, i) => {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    });
    return array;
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const bubblePeople = data.data.map((employee) => {
      const {
        attributes: { firstName, lastName, position, bio, photo, role },
      } = employee;

      return {
        name: `${firstName} ${lastName}`,
        imageSrc: photo?.data?.attributes?.url,
        position: position,
        positionKey: role as PositionKey,
        bio: bio,
      };
    });

    setBubbleData(shuffle(bubblePeople));
  }, [data]);

  const bubbleItems = getBubblesCoordinates(
    bubbleData,
    bubbleFilter,
    setBubbleFilter,
    {
      dev: t("bubbleContainer.dev"),
      pm: t("bubbleContainer.pm"),
      qa: t("bubbleContainer.qa"),
      spec: t("bubbleContainer.spec"),
      sales: t("bubbleContainer.sales"),
    }
  );

  return (
    <div {...htmlProps} className={[classes.bubble, "mt-8", "mb-8"].join(" ")}>
      <div className={classes.bubble__gridWrapper}>
        {bubbleItems.map((bubble, index) => {
          const isBubbleDisabled =
            bubbleFilter !== null && bubble.positionKey !== bubbleFilter;
          const updatedBubble = { ...bubble, disabled: isBubbleDisabled };
          return (
            <Bubble key={index} {...updatedBubble} filter={bubbleFilter} />
          );
        })}
      </div>
    </div>
  );
};

export default BubblesContainer;
